import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Clock, FileText, Plus, Share2 } from 'lucide-react';
import { apiClient } from '@/core/api/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Badge } from '@/shared/components/ui/badge';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { startNewCarePlan } from '../utils/navigatorSession';

type CarePlanListItem = {
  id: string;
  plan_name?: string;
  plan_description?: string;
  created_at?: string;
  session_id?: string;
  /** Set when this row is a care plan an FP shared with the user (Feature 077). */
  fp_qa_engagement_id?: string | null;
};

const formatDate = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

export const NavigatorHub = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const existingSessionId = sessionStorage.getItem('bquest_nav_session_id');
  const [isStartNewConfirmOpen, setIsStartNewConfirmOpen] = useState(false);
  // A returning user may have an in-progress chat and/or a completed plan that
  // "Start a New Care Guide" would silently discard — confirm first in that case.
  const hasResumable = Boolean(localStorage.getItem('chat_session_id') || existingSessionId);

  const { data: carePlans = [], isLoading } = useQuery<CarePlanListItem[]>({
    queryKey: ['navigator-care-plans'],
    enabled: isAuthenticated,
    queryFn: async () => {
      const { data } = await apiClient.get<CarePlanListItem[]>('/navigator/care-plans');
      return data ?? [];
    },
  });

  const startNew = () => startNewCarePlan(navigate);
  // Guard against discarding a resumable session by accident; first-time users
  // (nothing to lose) start fresh immediately.
  const handleStartNewClick = () => {
    if (hasResumable) {
      setIsStartNewConfirmOpen(true);
    } else {
      startNew();
    }
  };

  const continuePlan = (sessionId?: string) => {
    const target = sessionId ?? existingSessionId ?? undefined;
    if (target) {
      navigate('/care-navigator/results', { state: { sessionId: target } });
    } else {
      navigate('/care-navigator');
    }
  };

  // FP-shared plans are not session-backed; open the dedicated read-only view.
  const openPlan = (plan: CarePlanListItem) => {
    if (plan.fp_qa_engagement_id) {
      navigate(`/care-navigator/shared/${plan.fp_qa_engagement_id}`);
      return;
    }
    continuePlan(plan.session_id);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto max-w-5xl px-4 py-10">
        <header className="mb-8">
          <h1 className="font-termina text-3xl font-medium text-text-primary">Your Care Guides</h1>
          <p className="mt-2 text-text-secondary">Continue your care planning journey or start a new plan.</p>
        </header>

        <div className="mb-10 grid gap-4 md:grid-cols-2">
          <button
            type="button"
            onClick={handleStartNewClick}
            className="flex flex-col items-start gap-2 rounded-2xl border border-sage bg-sage p-6 text-left text-white transition-transform duration-150 ease-out hover:-translate-y-0.5"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15">
              <Plus className="h-5 w-5" aria-hidden />
            </span>
            <span className="font-termina text-xl font-medium">Start a New Care Guide</span>
            <span className="text-sm text-white/80">
              Our AI Navigator will guide you through a short Q&amp;A and assemble your matches.
            </span>
          </button>

          <button
            type="button"
            onClick={() => continuePlan()}
            disabled={!existingSessionId}
            className="flex flex-col items-start gap-2 rounded-2xl border border-border-subtle bg-card-base p-6 text-left text-text-primary transition-all duration-150 ease-out hover:-translate-y-0.5 hover:border-sage disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-sage-bg text-sage">
              <FileText className="h-5 w-5" aria-hidden />
            </span>
            <span className="font-termina text-xl font-medium">Continue Your Care Guide</span>
            <span className="text-sm text-text-secondary">Resume working on your most recent care guide.</span>
          </button>
        </div>

        {isAuthenticated && (
          <div className="space-y-3">
            {isLoading ? (
              [0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)
            ) : carePlans.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border-subtle bg-card-subtle px-6 py-10 text-center">
                <p className="font-termina text-lg font-medium text-text-primary">No care guides yet</p>
                <p className="mt-1 text-sm text-text-secondary">Start a new plan to see it here.</p>
              </div>
            ) : (
              carePlans.map((plan) => (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => openPlan(plan)}
                  className="flex w-full items-center justify-between gap-4 rounded-xl border border-border-subtle bg-card-base px-5 py-4 text-left transition-all duration-150 ease-out hover:border-sage"
                >
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-termina text-base font-medium text-text-primary">
                        {plan.plan_name || 'Care Guide'}
                      </span>
                      {plan.fp_qa_engagement_id && (
                        <Badge className="border-transparent bg-sage-bg text-sage">
                          <Share2 className="h-3 w-3" aria-hidden />
                          Shared by your advisor
                        </Badge>
                      )}
                    </span>
                    {plan.created_at && (
                      <span className="mt-1 flex items-center gap-1.5 text-xs text-text-tertiary">
                        <Clock className="h-3 w-3" aria-hidden />
                        Created {formatDate(plan.created_at)}
                      </span>
                    )}
                  </span>
                  <span className="text-sm font-medium text-sage">View &rarr;</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      <AlertDialog open={isStartNewConfirmOpen} onOpenChange={setIsStartNewConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start a new care guide?</AlertDialogTitle>
            <AlertDialogDescription>
              This starts a fresh Navigator chat from the beginning. Your in-progress chat and most
              recent guide won&apos;t be available to continue afterward.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-0 bg-[#026857] text-white hover:bg-[#025246] focus:ring-[#026857]">
              Keep my guide
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={startNew}
              className="bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-400"
            >
              Yes, start new
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
